/**
 * Premium Cart Animation Controller
 * Manages all cart animations for next-generation UX
 * Optimized for snappy, smooth animations without API delays
 */

class CartAnimationController {
    constructor() {
        this.previousCartState = null;
        this.animationQueue = [];
        this.isAnimating = false;
        this.pendingAddition = false;
        
        this.init();
    }
    
    init() {
        // Listen for cart events - use capture phase for earlier detection
        document.documentElement.addEventListener('cart:refresh', this.handleCartRefresh.bind(this), true);
        document.documentElement.addEventListener('cart:updated', this.handleCartUpdated.bind(this));
        
        // Listen for quantity changes
        document.addEventListener('click', (e) => {
            if (e.target.closest('.quantity-selector__button')) {
                this.handleQuantityButtonClick(e.target.closest('.quantity-selector__button'));
            }
        });
        
        // Listen for cart count updates
        this.observeCartCount();
        
        // Observe cart DOM changes for immediate animation application
        this.observeCartDOM();
        
        // Observe cart drawer open state for initial item marking
        this.observeCartDrawer();
        
        console.log('[CartAnimations] Controller initialized - optimized for performance');
    }
    
    /**
     * Observe cart drawer open state
     */
    observeCartDrawer() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;
        
        this._isDrawerOpen = cartDrawer.hasAttribute('open');

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                    if (cartDrawer.hasAttribute('open') && !this._isDrawerOpen) {
                        this.trackCartDrawerOpened();
                    } else if (!cartDrawer.hasAttribute('open') && this._isDrawerOpen) {
                        this.trackCartDrawerClosed();
                    }

                    this._isDrawerOpen = cartDrawer.hasAttribute('open');

                    if (this._isDrawerOpen) {
                        // Cart just opened - mark existing items as animated
                        requestAnimationFrame(() => {
                            const lineItems = cartDrawer.querySelectorAll('athena-line-item:not([data-animated])');
                            lineItems.forEach(item => {
                                item.setAttribute('data-animated', 'true');
                            });
                        });
                    }
                }
            }
        });
        
        observer.observe(cartDrawer, {
            attributes: true,
            attributeFilter: ['open']
        });
    }

    /**
     * Send cart_drawer_opened event to GA4 via gtag.
     */
    trackCartDrawerOpened() {
        if (typeof gtag !== 'function') return;

        const eventData = {
            page_path:  window.location.pathname,
            ac_session_id: window.SiteAnalytics?.sessionId || null,
            currency:   window.Shopify?.currency?.active || 'USD'
        };

        gtag('event', 'cart_drawer_opened', eventData);
    }

    /**
     * Send cart_drawer_closed event to GA4 via gtag.
     */
    trackCartDrawerClosed() {
        if (typeof gtag !== 'function') return;

        const eventData = {
            page_path:  window.location.pathname,
            ac_session_id: window.SiteAnalytics?.sessionId || null,
            currency:   window.Shopify?.currency?.active || 'USD'
        };

        gtag('event', 'cart_drawer_closed', eventData);
    }
    
    /**
     * Observe cart DOM for edge cases and fallback animation support
     * Primary animations are now handled in theme.js for better timing
     */
    observeCartDOM() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;
        
        const observer = new MutationObserver((mutations) => {
            // This observer now serves as a backup for edge cases
            // Primary animation logic is in theme.js for synchronous timing
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1) {
                            // Backup: If an item appears without animation class or data-animated,
                            // it might be from an edge case - handle it gracefully
                            if (node.tagName === 'ATHENA-LINE-ITEM') {
                                this.ensureItemIsVisible(node);
                            } else {
                                const lineItems = node.querySelectorAll?.('athena-line-item');
                                lineItems?.forEach(item => this.ensureItemIsVisible(item));
                            }
                        }
                    });
                }
            }
        });
        
        observer.observe(cartDrawer, {
            childList: true,
            subtree: true
        });
    }
    
    /**
     * Ensure item is visible - backup for edge cases
     */
    ensureItemIsVisible(item) {
        // If item doesn't have animation class or data-animated attribute,
        // it's from an edge case - make sure it's visible
        if (!item.classList.contains('cart-item-adding') && !item.hasAttribute('data-animated')) {
            // Mark it as animated so it becomes visible
            item.setAttribute('data-animated', 'true');
        }
    }
    
    /**
     * Handle cart refresh event
     * Note: Primary add animations are now handled in theme.js for better synchronous timing
     */
    async handleCartRefresh(event) {
        const currentCart = event.detail?.cart;
        
        if (!currentCart) return;
        
        // Shorter wait for snappier response
        await this.waitForDOMUpdate();
        
        // Handle secondary animations (prices, subtotal, etc.)
        if (this.previousCartState) {
            this.detectAndAnimate(this.previousCartState, currentCart);
        } else {
            // First load - ensure all items are marked as animated
            this.animateInitialLoad();
        }
        
        // Update previous state
        this.previousCartState = this.cloneCartState(currentCart);
    }
    
    /**
     * Handle cart updated event
     */
    handleCartUpdated(event) {
        // Skip animations if this is a subscription change (already handled inline)
        if (event.detail?.skipRerender) {
            console.log('[CartAnimations] Skipping animations for subscription change');
            return;
        }
        
        // Skip checkout button animation if this is a quantity change
        // (quantity changes shouldn't cause button to jump)
        if (!event.detail?.quantityChange) {
            this.animateCheckoutButton();
        }
        
        // Animate subtotal (only if not already animated inline)
        if (!event.detail?.sellingPlanChange && !event.detail?.quantityChange) {
            this.animateSubtotal();
        }
    }
    
    /**
     * Detect changes between old and new cart states
     */
    detectAndAnimate(oldCart, newCart) {
        const oldItems = oldCart.items || [];
        const newItems = newCart.items || [];
        
        const oldItemIds = new Set(oldItems.map(item => item.key));
        const newItemIds = new Set(newItems.map(item => item.key));
        
        // Find added items
        const addedItemIds = [...newItemIds].filter(id => !oldItemIds.has(id));
        
        // Find removed items (already animated by line-item-quantity component)
        const removedItemIds = [...oldItemIds].filter(id => !newItemIds.has(id));
        
        // Find updated items (quantity or property changes)
        const updatedItemIds = [];
        newItems.forEach(newItem => {
            const oldItem = oldItems.find(item => item.key === newItem.key);
            if (oldItem && (oldItem.quantity !== newItem.quantity || 
                           oldItem.selling_plan_allocation?.selling_plan?.id !== newItem.selling_plan_allocation?.selling_plan?.id)) {
                updatedItemIds.push(newItem.key);
            }
        });
        
        // Animate changes
        if (addedItemIds.length > 0) {
            this.animateAddedItems(addedItemIds);
            // When items are added, also trigger the update animation for just the new item
            this.animateUpdatedItems(addedItemIds);
        }
        
        if (updatedItemIds.length > 0 && addedItemIds.length === 0) {
            // Only animate updates if items weren't added
            // (to avoid double animation on new items)
            this.animateUpdatedItems(updatedItemIds);
        }
        
        // Don't animate all prices when adding items - too much visual noise
        if (addedItemIds.length === 0) {
            this.animatePrices();
        }
    }
    
    /**
     * Animate newly added items - now primarily handled in theme.js
     * This is kept as a fallback but shouldn't typically be called
     */
    animateAddedItems(itemIds) {
        // Primary animation is now handled synchronously in theme.js
        // This method is kept as a backup but shouldn't be needed in normal flow
        console.log('[CartAnimations] animateAddedItems called (should be handled by theme.js)');
    }
    
    /**
     * Animate updated items - only highlight items that were actually updated
     */
    animateUpdatedItems(itemIds) {
        // Don't animate if no specific items to update
        if (!itemIds || itemIds.length === 0) return;
        
        // For newly added items from optimistic updates, we want to highlight them
        // These will have the cart-item-adding class and we should add the pink flash after they appear
        requestAnimationFrame(() => {
            // Wait a moment for the add animation to complete
            setTimeout(() => {
                const newItems = document.querySelectorAll('athena-line-item.cart-item-adding');
                
                // If there are items being added (optimistic replacements), highlight only those
                if (newItems.length > 0) {
                    newItems.forEach(item => {
                        // Wait for add animation to finish, then add highlight
                        setTimeout(() => {
                            item.classList.add('cart-item-updating');
                            
                            setTimeout(() => {
                                item.classList.remove('cart-item-updating');
                            }, 600);
                        }, 500); // After add animation completes
                    });
                } else {
                    // For true updates (quantity/plan changes), we'd need to identify specific items
                    // For now, we'll skip the animation to avoid flashing all items
                    // This could be enhanced later with better item tracking
                }
            }, 50);
        });
    }
    
    /**
     * Animate price changes - quick and snappy
     */
    animatePrices() {
        const prices = document.querySelectorAll('.athena-line-item__price .price');
        
        prices.forEach(price => {
            price.classList.add('price-changing');
            
            setTimeout(() => {
                price.classList.remove('price-changing');
            }, 250);
        });
    }
    
    /**
     * Animate subtotal - quick and snappy
     */
    animateSubtotal() {
        const subtotal = document.querySelector('.mini-cart__subtotal-amount');
        
        if (subtotal) {
            subtotal.classList.add('updating');
            
            setTimeout(() => {
                subtotal.classList.remove('updating');
            }, 250);
        }
    }
    
    /**
     * Animate checkout button - quick and snappy
     */
    animateCheckoutButton() {
        const button = document.querySelector('.checkout-button');
        
        if (button) {
            button.classList.add('cart-updated');
            
            setTimeout(() => {
                button.classList.remove('cart-updated');
            }, 250);
        }
    }
    
    /**
     * Handle quantity button click
     */
    handleQuantityButtonClick(button) {
        // Add ripple effect
        button.classList.add('btn-ripple');
        
        setTimeout(() => {
            button.classList.remove('btn-ripple');
        }, 400);
        
        // Find and animate the quantity input
        const quantityInput = button.closest('.quantity-selector')?.querySelector('.quantity-selector__input');
        
        if (quantityInput) {
            setTimeout(() => {
                quantityInput.classList.add('value-changing');
                
                setTimeout(() => {
                    quantityInput.classList.remove('value-changing');
                }, 400);
            }, 100);
        }
    }
    
    /**
     * Observe cart count changes - snappy updates
     */
    observeCartCount() {
        const cartCounts = document.querySelectorAll('cart-count');
        
        cartCounts.forEach(countEl => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' || mutation.type === 'characterData') {
                        countEl.classList.add('updating');
                        
                        setTimeout(() => {
                            countEl.classList.remove('updating');
                        }, 250);
                    }
                });
            });
            
            observer.observe(countEl, {
                childList: true,
                characterData: true,
                subtree: true
            });
        });
    }
    
    /**
     * Animate initial cart load
     */
    animateInitialLoad() {
        // On initial load, mark all existing items as already animated
        // This prevents them from being invisible or animating on cart open
        const lineItems = document.querySelectorAll('athena-line-item');
        lineItems.forEach(item => {
            if (!item.hasAttribute('data-animated')) {
                item.setAttribute('data-animated', 'true');
            }
        });
    }
    
    /**
     * Wait for DOM to update after cart refresh - optimized for speed
     */
    waitForDOMUpdate() {
        return new Promise(resolve => {
            // Reduced from 100ms to 50ms for snappier response
            setTimeout(resolve, 50);
        });
    }
    
    /**
     * Clone cart state for comparison
     */
    cloneCartState(cart) {
        if (!cart) return null;
        
        return {
            items: cart.items?.map(item => ({
                key: item.key,
                id: item.id,
                quantity: item.quantity,
                selling_plan_allocation: item.selling_plan_allocation
            })) || [],
            item_count: cart.item_count,
            total_price: cart.total_price
        };
    }
    
    /**
     * Add loading state to line item
     */
    static setLineItemLoading(lineItem, loading = true) {
        if (loading) {
            lineItem.classList.add('is-loading');
        } else {
            lineItem.classList.remove('is-loading');
            
            // Show success checkmark briefly
            const checkmark = lineItem.querySelector('.line-item__loader-mark');
            if (checkmark) {
                checkmark.removeAttribute('hidden');
                setTimeout(() => {
                    checkmark.setAttribute('hidden', '');
                }, 800);
            }
        }
    }
    
    /**
     * Animate item removal (call before removing)
     */
    static animateItemRemoval(lineItem) {
        return new Promise(resolve => {
            lineItem.classList.add('cart-item-removing');
            
            // Wait for animation to complete
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }
}

// Initialize when DOM is ready
if (!window.cartAnimationController) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.cartAnimationController = new CartAnimationController();
        });
    } else {
        window.cartAnimationController = new CartAnimationController();
    }
}

// Export for use in other scripts
window.CartAnimationController = CartAnimationController;

